import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  TableHead,
} from "@mui/material";
import moment from "moment";
import EuropeaReceiptHeader from "./Header";
import { shouldUseNewPdfFormat } from "utils/shouldUseNewPdfFormat";
import convertImageToBase64 from "utils/convertImageToBase64";

const ClientGlobalInvoice = (props) => {
  const { reservationData, companyInfo, contentRef } = props;

  const InfoRow = ({ label, value }) => (
    <TableRow sx={{ border: "none" }}>
      <TableCell
        component="th"
        scope="row"
        sx={{
          border: "none",
          padding: "2px 0",
          width: "160px",
          verticalAlign: "top",
          fontSize: "13px",
        }}
      >
        {label}
      </TableCell>
      <TableCell
        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
        sx={{
          border: "none",
          padding: "2px 0",
          fontWeight: label === "E-mail" ? "bold" : "normal",
          fontSize: "13px",
        }}
      >
        {label === "E-mail:" ? (
          <a style={{ color: "black" }} href={`mailto:${value}`}>
            {value}
          </a>
        ) : (
          value
        )}
      </TableCell>
    </TableRow>
  );
  const [logoBase64, setLogoBase64] = useState("");

  const formatDateRange = () => {
    return `${moment(reservationData?.start_date).format("DD/MM/YY")} / ${moment(
      reservationData?.end_date
    ).format("DD/MM/YY")}`;
  };

  // gross_amount / city_tax may arrive as number or string depending on source.
  const total =
    Number(String(reservationData?.gross_amount ?? 0).replace(",", ".")) +
    Number(String(reservationData?.city_tax ?? 0).replace(",", "."));
  const labelCell = {
    border: "1px solid #e0e0e0",
    fontWeight: "bold",
    width: "20%",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    padding: "6px 10px",
    fontSize: "13px",
    verticalAlign: "middle",
  };

  const valueCell = {
    border: "1px solid #e0e0e0",
    width: "30%",
    padding: "6px 10px",
    fontSize: "13px",
    verticalAlign: "middle",
  };

  const headerCell = {
    border: "1px solid #e0e0e0",
    fontWeight: "bold",
    borderBottom: "none",
    padding: "6px 8px",
    fontSize: "13px",
    position: "relative",
    top: "-5px",
  };

  const bodyCell = {
    border: "1px solid #e0e0e0",
    verticalAlign: "middle",
    padding: "6px 8px",
    fontSize: "13px",
  };

  const emptyCell = {
    border: "1px solid #999",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  };

  const noPageBreak = {
    pageBreakInside: "avoid",
    breakInside: "avoid",
    WebkitColumnBreakInside: "avoid",
  };
  const endDate = moment(reservationData?.end_date);
  const month = endDate.month() + 1;
  const year = endDate.year();
  const newPdfFormat = shouldUseNewPdfFormat(month, year);
  const toNumber = (val) => {
    const num = parseFloat(String(val).replace(",", "."));
    return isNaN(num) ? 0 : num;
  };
  const totalAmount =
    toNumber(reservationData?.rent) +
    toNumber(reservationData?.cleaning_fees) +
    toNumber(reservationData?.utilities_fees) +
    toNumber(reservationData?.extra_fees) +
    toNumber(reservationData?.city_tax) +
    toNumber(reservationData?.vat_on_rent);

  const getCompanyLogo = async () => {
    try {
      if (companyInfo?.logo) {
        const base64 = await convertImageToBase64(companyInfo?.logo);
        if (base64) {
          setLogoBase64(base64);
        }
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  useEffect(() => {
    if (companyInfo?.logo) {
      getCompanyLogo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);
  return (
    <>
      {!newPdfFormat ? (
        <Box
          sx={{ margin: "auto", p: 1, borderRadius: 2, bgcolor: "white" }}
          ref={contentRef}
        >
          <EuropeaReceiptHeader
            title={`RESERVATION INVOICE - N°: ${reservationData?.company_invoice}`}
          />
          <Box my={2}>
            <Grid container spacing={2}>
              <Grid sx={{ px: 2, py: 2 }} md={6} sm={6}>
                <Typography variant="subtitle2" color="gray">
                  The Company
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Table size="small">
                  <TableBody>
                    <InfoRow
                      label="Company Name:"
                      value={companyInfo?.company || "Europea Residences SARL"}
                    />
                    <InfoRow
                      label="Business number:"
                      value={companyInfo?.business_number}
                    />
                    <InfoRow label="Address:" value={companyInfo?.address} />
                    <InfoRow label="Zip code:" value={companyInfo?.zip_code} />
                    <InfoRow label="City:" value={companyInfo?.city} />
                    <InfoRow label="Country:" value={companyInfo?.country} />
                    <InfoRow
                      sx={{ whitespace: "nowrap" }}
                      label="E-mail:"
                      value={"accounting@europea-residences.com"}
                    />
                  </TableBody>
                </Table>
              </Grid>
              <Grid sx={{ px: 2, py: 2 }} md={6} sm={6}>
                <Typography variant="subtitle2" color="gray">
                  The Client
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Table size="small">
                  <TableBody>
                    <InfoRow
                      label="Name:"
                      value={
                        reservationData?.guestName ||
                        reservationData?.guest_name
                      }
                    />
                    <InfoRow label="Company Name:" value={""} />
                    <InfoRow label="Business number:" value={""} />
                    <InfoRow label="Address:" value={""} />
                    <InfoRow label="Zip code:" value={""} />
                    <InfoRow label="City:" value={""} />
                    <InfoRow label="Country:" value={""} />
                    <InfoRow
                      label="Telephone:"
                      value={reservationData?.guest_phone}
                    />
                    <InfoRow
                      label="E-mail:"
                      value={reservationData?.guest_email}
                    />
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </Box>

          {/* Receipt Info */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="gray">
            Reservation Invoice - N°: {reservationData?.company_invoice}{" "}
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Box sx={{ marginTop: 4 }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Residence</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Stay dates</strong>
                    </TableCell>
                    <TableCell>
                      <strong>N° Tenants</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Category</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Rent</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell style={{ width: "200px" }}>
                      {" "}
                      {reservationData?.property_id?.title ||
                        reservationData?.property_name ||
                        ""}{" "}
                    </TableCell>
                    <TableCell>{formatDateRange()}</TableCell>
                    <TableCell>{reservationData?.number_of_person}</TableCell>
                    <TableCell align="rights">€ {"Rent"}</TableCell>
                    <TableCell align="rights">
                      €{" "}
                      <strong>
                        {" "}
                        {toNumber(reservationData?.rent)
                          .toFixed(2)
                          .replace(".", ",")}{" "}
                      </strong>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    sx={{
                      height: "auto",
                      "& td, & th": { paddingY: "3px" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      colSpan={3}
                    ></TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                    >
                      {" "}
                      {"Cleaning fees"}{" "}
                    </TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      align="rights"
                    >
                      €
                      {toNumber(reservationData?.cleaning_fees)
                        .toFixed(2)
                        .replace(".", ",")}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "auto",
                      "& td, & th": { paddingY: "3px" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      colSpan={3}
                    ></TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                    >
                      {" "}
                      {"Extra fees"}{" "}
                    </TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      align="rights"
                    >
                      {" "}
                      €
                      {toNumber(reservationData?.extra_fees)
                        .toFixed(2)
                        .replace(".", ",")}{" "}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "auto",
                      "& td, & th": { paddingY: "3px" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      colSpan={3}
                    ></TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                    >
                      {" "}
                      {"Services fees"}{" "}
                    </TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      align="rights"
                    >
                      {" "}
                      €
                      {toNumber(reservationData?.service_fees)
                        .toFixed(2)
                        .replace(".", ",")}{" "}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "auto",
                      "& td, & th": { paddingY: "3px" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      colSpan={3}
                    ></TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                    >
                      {" "}
                      {"City Tax"}{" "}
                    </TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      align="rights"
                    >
                      {" "}
                      €
                      {toNumber(reservationData?.city_tax)
                        .toFixed(2)
                        .replace(".", ",")}{" "}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      height: "auto",
                      "& td, & th": { paddingY: "3px" },
                      borderBottom: "none",
                    }}
                  >
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      colSpan={3}
                    ></TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                    >
                      {" "}
                      {" TOTAL"}{" "}
                    </TableCell>
                    <TableCell
                      sx={{ padding: "4px auto", borderBottom: "none" }}
                      align="rights"
                    >
                      <strong>
                        {" "}
                        €
                        {String(Number(total).toFixed(2)).replace(
                          ".",
                          ",",
                        )}{" "}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between">
            <Typography fontSize={15} variant="caption" color="black">
              Please note that our total does not include channel commissions
              from Airbnb, Booking, Expedia, etc., except for Abritel/VRBO
              reservations{" "}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: "white" }} ref={contentRef}>
          <EuropeaReceiptHeader
            title={`RESERVATION INVOICE - N°: ${reservationData?.company_invoice}`}
            logo={companyInfo?.logo}
          />
          <Box mt={1} sx={noPageBreak}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ ...noPageBreak, mt: 3, borderRadius: 0 }}
            >
              <Table
                sx={{
                  border: "1px solid #999",
                  borderCollapse: "collapse",
                }}
              >
                {/* HEADER */}
                <TableBody>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: "50%",
                        border: "1px solid #e0e0e0",
                        borderBottom: "none",
                        padding: "6px 8px",
                        fontSize: "13px",
                      }}
                    >
                      <span style={{ position: "relative", top: "-5px" }}>
                        Management Company
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        width: "50%",
                        border: "1px solid #e0e0e0",
                        borderBottom: "none",
                        padding: "6px 8px",
                        fontSize: "13px",
                      }}
                    >
                      <span style={{ position: "relative", top: "-5px" }}>
                        Buyer ( Tenant )
                      </span>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {/* LEFT COLUMN */}
                    <TableCell
                      sx={{
                        border: "1px solid #e0e0e0",
                        padding: "6px 8px",
                        fontSize: "13px",
                        verticalAlign: "top",
                      }}
                    >
                      <Table size="small">
                        <TableBody>
                          <InfoRow
                            label="Company:"
                            value={companyInfo?.company || ""}
                          />
                          <InfoRow
                            label="VAT / Registration Number:"
                            value={companyInfo?.vat_number || ""}
                          />
                          <InfoRow
                            label="Address:"
                            value={companyInfo?.address || ""}
                          />
                          <InfoRow
                            label="Postal Code:"
                            value={companyInfo?.zip_code || ""}
                          />
                          <InfoRow
                            label="Country:"
                            value={companyInfo?.country || ""}
                          />
                          <InfoRow
                            label="Managing Director:"
                            value="Le Metayer Yann"
                          />
                          <InfoRow
                            label="Email:"
                            value={companyInfo?.email || ""}
                          />
                        </TableBody>
                      </Table>
                    </TableCell>

                    {/* RIGHT COLUMN */}
                    <TableCell
                      sx={{
                        border: "1px solid #e0e0e0",
                        padding: "6px 8px",
                        fontSize: "13px",
                        verticalAlign: "top",
                      }}
                    >
                      <Table size="small">
                        <TableBody>
                          <InfoRow
                            label="Name:"
                            value={
                              reservationData?.guestName ||
                              reservationData?.guest_name ||
                              ""
                            }
                          />
                          <InfoRow
                            label="VAT / Registration Number:"
                            value={reservationData?.client_vat_no || ""}
                          />
                          <InfoRow label="Address:" value={""} />
                          <InfoRow label="Postal Code:" value={""} />
                          <InfoRow label="City:" value={""} />
                          <InfoRow
                            label="Email:"
                            value={reservationData?.guest_email || ""}
                          />
                          <InfoRow
                            label="Tel:"
                            value={reservationData?.guest_phone || ""}
                          />
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {/**  Invoice period */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ ...noPageBreak, my: 3, borderRadius: 0 }}
          >
            <Table
              sx={{
                border: "1px solid #999",
                borderCollapse: "collapse",
                tableLayout: "fixed",
                width: "100%",
              }}
            >
              <TableBody>
                {/* ROW 1 */}
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={labelCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Invoice ID
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      {reservationData?.company_invoice}
                    </span>
                  </TableCell>
                  <TableCell sx={labelCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Stay Dates
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      {formatDateRange()}
                    </span>
                  </TableCell>
                </TableRow>

                {/* ROW 2 */}
                <TableRow>
                  <TableCell sx={labelCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Invoicing period
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      {moment(reservationData?.start_date).format("MMMM YYYY")}
                    </span>
                  </TableCell>
                  <TableCell sx={labelCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Residence
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      {reservationData?.property_details?.title ||
                        reservationData?.property_id?.title ||
                        ""}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Invoice body */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ ...noPageBreak, borderRadius: 0 }}
            my={3}
          >
            <Table
              sx={{
                border: "1px solid #999",
                borderCollapse: "collapse",
              }}
            >
              {/* HEADER */}
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell
                    sx={{ ...headerCell, padding: "16px 8px" }}
                    style={{ width: "40%" }}
                  >
                    <span style={{ position: "relative", top: "-5px" }}>
                      Description
                    </span>
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCell, padding: "16px 8px" }}
                    style={{ width: "35%" }}
                  >
                    <span style={{ position: "relative", top: "-5px" }}>
                      Nature
                    </span>
                  </TableCell>
                  <TableCell sx={{ ...headerCell, padding: "16px 8px" }}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Unit price
                    </span>
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* BODY */}
              <TableBody>
                {/* MAIN LINE */}
                <TableRow>
                  <TableCell sx={bodyCell}>
                    <Typography sx={{ fontSize: "13px" }}>
                      <span style={{ position: "relative", top: "-5px" }}>
                        {`Travel service - Stay at ${
                          reservationData?.property_details?.title ||
                          reservationData?.property_id?.title ||
                          ""
                        }`}
                      </span>
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Accommodation
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell} style={{ fontWeight: "normal" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.rent || "0,00")
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>

                <TableRow sx={noPageBreak}>
                  <TableCell sx={{ ...emptyCell, border: "none" }} />
                  <TableCell sx={bodyCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Service fees
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell} style={{ fontWeight: "normal" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.cleaning_fees || "0,00")
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>

                <TableRow sx={noPageBreak}>
                  <TableCell sx={{ ...emptyCell, border: "none" }} />
                  <TableCell sx={bodyCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Utilities
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell} style={{ fontWeight: "normal" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.utilities_fees || "0,00")
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
                { toNumber(reservationData?.extra_fees || 0) > 0 && (
                  <TableRow sx={noPageBreak}>
                    <TableCell sx={{ ...emptyCell, border: "none" }} />
                    <TableCell sx={bodyCell}>
                      <span style={{ position: "relative", top: "-5px" }}>
                        Extra fees
                      </span>
                    </TableCell>
                    <TableCell sx={valueCell} style={{ fontWeight: "normal" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          transform: "translateY(-5px)",
                        }}
                      >
                        <span>€</span>
                        <span>
                          {toNumber(reservationData?.extra_fees || "0,00")
                            .toFixed(2)
                            .replace(".", ",")}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={noPageBreak}>
                  <TableCell sx={{ ...emptyCell, border: "none" }} />
                  <TableCell sx={bodyCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      City Tax
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell} style={{ fontWeight: "normal" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.city_tax || "0,00")
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>

                <TableRow sx={noPageBreak}>
                  <TableCell sx={{ ...emptyCell, border: "none" }} />
                  <TableCell sx={bodyCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      VAT
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell} style={{ fontWeight: "normal" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.vat_on_rent)
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>

                <TableRow sx={noPageBreak}>
                  <TableCell sx={bodyCell} style={{ fontWeight: "bold" }}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      TOTAL
                    </span>
                  </TableCell>
                  <TableCell sx={bodyCell} />
                  <TableCell sx={valueCell} style={{ fontWeight: "bold" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>{totalAmount.toFixed(2).replace(".", ",")}</span>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/** VAT summary table */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ ...noPageBreak, my: 3, borderRadius: 0 }}
          >
            <Table
              sx={{
                border: "1px solid #999",
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                {/* ROW 1 */}
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={{ ...valueCell, fontWeight: "bold" }}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      VAT category
                    </span>
                  </TableCell>
                  <TableCell sx={{ ...valueCell, fontWeight: "bold" }}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      Taxable amount
                    </span>
                  </TableCell>
                  <TableCell sx={{ ...valueCell, fontWeight: "bold" }}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      VAT amount
                    </span>
                  </TableCell>
                </TableRow>

                {/* ROW 2 */}
                <TableRow>
                  <TableCell sx={valueCell}>
                    <span style={{ position: "relative", top: "-5px" }}>
                      {toNumber(reservationData?.vat_on_rent) > 0
                        ? "Standard Rate"
                        : "Residential rental"}
                    </span>
                  </TableCell>
                  <TableCell sx={valueCell}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.rent || "0,00")
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell sx={valueCell}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <span>€</span>
                      <span>
                        {toNumber(reservationData?.vat_on_rent)
                          .toFixed(2)
                          .replace(".", ",")}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography
            variant="subtitle1"
            color="black"
            mt={2}
          >{`Notes / Legal`}</Typography>
          <Typography
            fontSize={15}
            variant="caption"
            color="black"
            style={{ letterSpacing: "0px" }}
          >
            Europea Residences is responsible for the collection and payment of
            the City Tax to the competent local authorities, in compliance with
            applicable legislation.{" "}
          </Typography>
        </Box>
      )}{" "}
    </>
  );
};

export default ClientGlobalInvoice;
